import { FormEvent, useEffect, useState } from "react";
import { api, Contact, DialList } from "../lib/api";
import * as XLSX from "xlsx";

export function ContactsPage({ csrf }: { csrf: string }) {
  const [items, setItems] = useState<Contact[]>([]);
  const [lists, setLists] = useState<DialList[]>([]);
  const [q, setQ] = useState("");
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [form, setForm] = useState({ phone: "", firstName: "", lastName: "", email: "", company: "" });
  const [csv, setCsv] = useState("");
  const [uploading, setUploading] = useState(false);
  const [listName, setListName] = useState("");
  const [dnc, setDnc] = useState<import("../lib/api").DncEntry[]>([]);
  const [dncPhone, setDncPhone] = useState("");

  async function refresh(search = q) {
    const [contacts, listRes, dncRes] = await Promise.all([
      api.listContacts(search),
      api.listLists(),
      api.listDnc()
    ]);
    setItems(contacts.items);
    setLists(listRes.lists);
    setDnc(dncRes.dnc);
  }

  useEffect(() => {
    refresh().catch((e) => setError(e.message));
  }, []);

  async function onSave(e: FormEvent) {
    e.preventDefault();
    setError("");
    setMsg("");
    try {
      await api.saveContact(form, csrf);
      setForm({ phone: "", firstName: "", lastName: "", email: "", company: "" });
      setMsg("Contact saved");
      await refresh();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Save failed");
    }
  }

  async function onImport(e: FormEvent) {
    e.preventDefault();
    setError("");
    setMsg("");
    try {
      const lines = csv
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);
      const contacts = lines.map((line) => {
        const [phone, firstName = "", lastName = "", email = "", company = ""] = line
          .split(",")
          .map((p) => p.trim());
        return { phone, firstName, lastName, email, company };
      });
      const result = await api.importContacts(contacts, null, csrf);
      setMsg(`Imported ${result.imported}, skipped ${result.skipped}`);
      setCsv("");
      await refresh();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Import failed");
    }
  }

  async function onFileUpload(file: File) {
    setError("");
    setMsg("");
    setUploading(true);
    try {
      if (file.size > 10 * 1024 * 1024) throw new Error("Contact file must be 10 MB or smaller");
      const ext = file.name.toLowerCase().split(".").pop();
      if (!ext || !["csv", "xls", "xlsx"].includes(ext)) throw new Error("Choose a CSV, XLS, or XLSX file");
      const workbook = XLSX.read(await file.arrayBuffer(), { type: "array", cellDates: false });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      if (!sheet) throw new Error("The workbook has no sheets");
      const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: "", raw: false });
      if (rows.length < 2) throw new Error("The file must include a header row and at least one contact");
      const headers = rows[0].map((h) => String(h ?? "").trim().toLowerCase().replace(/[^a-z0-9]+/g, ""));
      const index = (...names: string[]) => names.map((name) => headers.indexOf(name)).find((i) => i >= 0) ?? -1;
      const phoneIndex = index("phone", "phonenumber", "mobile", "telephone", "tel");
      if (phoneIndex < 0) throw new Error("A phone column is required");
      const firstIndex = index("firstname", "first");
      const lastIndex = index("lastname", "last");
      const emailIndex = index("email", "emailaddress");
      const companyIndex = index("company", "companyname", "organization");
      const fullNameIndex = index("name", "fullname", "contactname");
      const contacts = rows.slice(1).map((row) => {
        const value = (i: number) => (i >= 0 ? String(row[i] ?? "").trim() : "");
        const fullName = value(fullNameIndex);
        const parts = fullName ? fullName.split(/\s+/) : [];
        return {
          phone: value(phoneIndex),
          firstName: value(firstIndex) || parts.shift() || "",
          lastName: value(lastIndex) || parts.join(" "),
          email: value(emailIndex),
          company: value(companyIndex)
        };
      }).filter((contact) => contact.phone);
      if (!contacts.length) throw new Error("No rows with phone numbers were found");
      const result = await api.importContacts(contacts, null, csrf);
      setMsg(`Imported ${result.imported}, skipped ${result.skipped}`);
      await refresh();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "File import failed");
    } finally {
      setUploading(false);
    }
  }

  async function createList(e: FormEvent) {
    e.preventDefault();
    if (!listName.trim()) return;
    await api.saveList({ name: listName.trim() }, csrf);
    setListName("");
    await refresh();
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>CRM contacts</h1>
          <p>Manage dial lists and contact records for high-volume campaigns.</p>
        </div>
      </div>
      {error ? <div className="alert error">{error}</div> : null}
      {msg ? <div className="alert success">{msg}</div> : null}

      <div className="card">
        <div className="section-title">
          <h2>Contacts ({items.length})</h2>
          <input
            style={{ maxWidth: 260 }}
            placeholder="Search phone, name, email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") refresh(q).catch((err) => setError(err.message));
            }}
          />
        </div>
        <table>
          <thead>
            <tr>
              <th>Phone</th>
              <th>Name</th>
              <th>Company</th>
              <th>Email</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id}>
                <td className="mono">{c.phone}</td>
                <td>
                  {c.firstName} {c.lastName}
                </td>
                <td>{c.company}</td>
                <td>{c.email}</td>
                <td>
                  <button
                    className="btn ghost sm danger"
                    type="button"
                    onClick={async () => {
                      await api.deleteContact(c.id, csrf);
                      await refresh();
                    }}
                  >
                    Delete
                  </button>{" "}
                  <button
                    className="btn ghost sm"
                    type="button"
                    onClick={async () => {
                      await api.addDnc({ phone: c.phone, reason: "manual" }, csrf);
                      setMsg(`${c.phone} added to DNC`);
                      await refresh();
                    }}
                  >
                    DNC
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h2>Add contact</h2>
        <form onSubmit={onSave}>
          <div className="form-grid">
            <label>
              <strong>Phone *</strong>
              <input
                required
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+18765551234"
              />
            </label>
            <label>
              <strong>First name</strong>
              <input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
            </label>
            <label>
              <strong>Last name</strong>
              <input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
            </label>
            <label>
              <strong>Email</strong>
              <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </label>
            <label>
              <strong>Company</strong>
              <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
            </label>
          </div>
          <button className="btn" type="submit">
            Save contact
          </button>
        </form>
      </div>

      <div className="card">
        <h2>Import contacts</h2>
        <p className="muted">Upload CSV, XLS, or XLSX files with a phone column. Optional columns: firstName, lastName, email, company.</p>
        <label>
          <strong>Contact file</strong>
          <input
            type="file"
            accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            disabled={uploading}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void onFileUpload(file);
              e.currentTarget.value = "";
            }}
          />
        </label>
        <p className="muted">Maximum file size: 10 MB.</p>
        {uploading ? <p className="muted">Importing contacts…</p> : null}
        <p className="muted">Or paste one contact per line: phone,firstName,lastName,email,company</p>
        <form onSubmit={onImport}>
          <textarea
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            placeholder={"+18765550001,Jane,Doe,jane@example.com,Acme"}
          />
          <button className="btn" type="submit">
            Import contacts
          </button>
        </form>
      </div>

      <div className="card">
        <h2>Lists</h2>
        <ul>
          {lists.map((l) => (
            <li key={l.id}>
              {l.name} <span className="muted mono">{l.id.slice(0, 8)}</span>
            </li>
          ))}
        </ul>
        <form onSubmit={createList} className="row-actions">
          <input
            style={{ maxWidth: 280 }}
            placeholder="New list name"
            value={listName}
            onChange={(e) => setListName(e.target.value)}
          />
          <button className="btn ghost" type="submit">
            Create list
          </button>
        </form>
      </div>

      <div className="card">
        <h2>Do Not Call</h2>
        <p className="muted">
          Global suppression. Numbers here are never autodialed. DTMF 9 during a PBX campaign also adds the
          caller here.
        </p>
        <form
          className="row-actions"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!dncPhone.trim()) return;
            await api.addDnc({ phone: dncPhone.trim(), reason: "manual" }, csrf);
            setDncPhone("");
            setMsg("Number added to DNC");
            await refresh();
          }}
        >
          <input
            style={{ maxWidth: 280 }}
            placeholder="+18765551234"
            value={dncPhone}
            onChange={(e) => setDncPhone(e.target.value)}
          />
          <button className="btn ghost" type="submit">
            Add to DNC
          </button>
        </form>
        {dnc.length === 0 ? (
          <p className="muted">No DNC numbers yet.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Phone</th>
                <th>Scope</th>
                <th>Reason</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {dnc.map((row) => (
                <tr key={row.id}>
                  <td className="mono">{row.phone}</td>
                  <td>{row.scope}</td>
                  <td>{row.reason}</td>
                  <td>
                    <button
                      className="btn ghost sm"
                      type="button"
                      onClick={async () => {
                        await api.removeDnc(row.id, csrf);
                        await refresh();
                      }}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
